<template>
  <b-modal
    v-model="editorShown"
    size="md-lg"
    centered
    scrollable
    no-close-on-backdrop
    header-bg-variant="light"
    header-class="p-0 d-block shadow-form bg-purple"
    footer-class="p-0 bg-extra-light-gray shadow-form"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
        style="padding: 10px 20px;"
      >
        <div>
          <h5 class="modal-title font-weight-bold">{{ modalTitle }}</h5>
          <div class="modal-subtitle">{{ modalSubtitle }}</div>
        </div>
        <button
          type="button"
          aria-label="Close"
          class="close"
          style="opacity:1 !important"
          @click="editorShown = false"
        >
          ×
        </button>
      </div>
    </template>
    <template v-slot:modal-footer>
      <div
        class="d-flex justify-content-between align-items-center w-100 m-0"
        style="padding: 10px 20px;"
      >
        <div>
          <b-button class="mr-2" variant="primary" @click="printDocument">
            Print
          </b-button>
          <b-button
            class="mr-2"
            variant="primary"
            :disabled="downloadingPDF"
            :style="{ cursor: downloadingPDF ? 'wait' : 'pointer' }"
            @click="savePdf"
          >
            Download PDF
          </b-button>
          <b-button class="mr-2" variant="primary" @click="sendFax">
            Send Fax
          </b-button>
        </div>
        <div style="font-size: 20px">
          <i
            @click="addDocumentZoomArea()"
            class="fa fa-search-plus zoom-control hover-text-primary"
          ></i>
          <i
            @click="lessDocumentZoomArea()"
            class="fa fa-search-minus zoom-control hover-text-primary"
          ></i>
        </div>
        <div>
          <b-button @click="editorShown = false" variant="secondary">
            Close
          </b-button>
        </div>
      </div>
    </template>
    <div class="h-100 w-100 text-center preview-modal">
      <zoom-area
        ref="documentZoomArea"
        :hideControls="true"
        width="1125px"
        class="h-100"
      >
        <vue-html2pdf
          :show-layout="false"
          :float-layout="false"
          :pdf-quality="2"
          :pdf-content-width="'100%'"
          :paginate-elements-by-height="1500"
          class="w-100"
        >
          <template slot="pdf-content">
            <div v-if="html" v-html-sanitize="html" class="m-4" />
          </template>
        </vue-html2pdf>
      </zoom-area>
      <vue-html2pdf
        v-show="false"
        :show-layout="false"
        :float-layout="false"
        :enable-download="true"
        :pdf-format="'letter'"
        :pdf-orientation="'portrait'"
        :pdf-content-width="'1125px'"
        :paginate-elements-by-height="1500"
        :html-to-pdf-options="pdfOptions"
        ref="html2Pdf"
        class="w-100"
        @hasDownloaded="downloadingPDF = false"
      >
        <template slot="pdf-content">
          <div
            v-if="html"
            v-html-sanitize="html"
            class="m-4"
            style="transform: scale(0.6625); transform-origin: 0% 0% 0px;"
          />
        </template>
      </vue-html2pdf>
    </div>
    <send-fax-modal
      v-if="showFaxModal"
      v-model="showFaxModal"
      :ids="this.ids"
      :sendFaxMethod="this.sendFaxMethod"
    ></send-fax-modal>
  </b-modal>
</template>

<script>
import { mapActions } from "vuex";
import VueHtml2pdf from "vue-html2pdf";
import documentServer from "../../server/document";
import eocServer from "../../server/eoc";
import ZoomArea from "../controls/ZoomArea";
import SendFaxModal from "../fax/SendFaxModal.vue";

export default {
  name: "EocViewAllDocumentsModal",
  components: { ZoomArea, VueHtml2pdf, SendFaxModal },
  props: ["eocId"],
  data() {
    return {
      ids: [],
      sendFaxMethod: "",
      eoc: null,
      blobUrl: null,
      html: null,
      downloadingPDF: false,
      showFaxModal: false
    };
  },
  async mounted() {
    this.eoc = await eocServer.get(this.eocId);
    const url = documentServer.eocDocumentsViewUrl(this.eocId);
    fetch(url.url, { headers: url.headers }).then(response => {
      var clone = response.clone();

      response.blob().then(blob => {
        this.blobUrl = URL.createObjectURL(blob);
      });

      clone.text().then(html => {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        this.html = doc.body.innerHTML
          .replace(/vh-100/gi, "")
          .replace(
            /document-single"/gi,
            `document-single" style="margin-bottom: 32px;"`
          );
      });
    });
  },
  methods: {
    ...mapActions("layout", ["show", "hide"]),
    async printDocument() {
      const iframe = document.createElement("iframe");
      iframe.style.width = "2250px";
      iframe.src = this.blobUrl;
      iframe.onload = function() {
        iframe.contentWindow.onafterprint = function() {
          document.body.removeChild(iframe);
        };

        setTimeout(() => {
          iframe.contentWindow.print();
        }, 500);
      };

      document.body.appendChild(iframe);
    },
    savePdf() {
      this.downloadingPDF = true;
      const filename = this.pdfOptions.filename;

      this.show();
      documentServer
        .downloadEocPdf(this.eocId)
        .then(res => {
          res.blob().then(blob => {
            let fileURL = URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.href = fileURL;
            link.download = filename;
            link.dispatchEvent(new MouseEvent("click"));
          });
        })
        .finally(() => {
          this.hide();
          this.downloadingPDF = false;
        });
    },
    sendFax() {
      this.ids = [this.eocId];
      this.sendFaxMethod = "sendEocPdf";
      this.showFaxModal = true;
    },
    addDocumentZoomArea() {
      this.$refs.documentZoomArea.addZoom();
    },
    lessDocumentZoomArea() {
      this.$refs.documentZoomArea.lessZoom();
    }
  },
  computed: {
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    modalTitle() {
      return "All Patient Documents";
    },
    modalSubtitle() {
      return this.eoc
        ? this.formatEoc(
            " | " + this.eoc.patient.firstName,
            this.eoc.patient.middleName,
            this.eoc.patient.preferredName,
            this.eoc.patient.lastName,
            this.eoc.patient.mrn,
            this.eoc.eocn
          )
        : "";
    },
    pdfOptions() {
      return {
        image: { type: "jpeg", quality: 1 },
        margin: [4, 4, 10, 4],
        filename: `All documents ${this.modalSubtitle}`,
        html2canvas: { scale: 4 }
      };
    }
  }
};
</script>

<style scoped>
.preview-modal {
  min-height: calc(85vh - 120px);
}
.zoom-control {
  width: 37px;
}
.zoom-control:hover {
  font-size: 21px;
  cursor: pointer;
}
</style>
